import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useEventsModule } from '../context/EventsModuleContext';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// All languages supported by Google Translate that might be enabled
const ALL_SUPPORTED_LANGUAGES = {
  'sv': { name: 'Svenska', flag: '🇸🇪' },
  'en': { name: 'English', flag: '🇬🇧' },
  'pl': { name: 'Polski', flag: '🇵🇱' },
  'es': { name: 'Español', flag: '🇪🇸' },
  'ar': { name: 'العربية', flag: '🇱🇧' },
  'de': { name: 'Deutsch', flag: '🇩🇪' },
  'fr': { name: 'Français', flag: '🇫🇷' },
  'it': { name: 'Italiano', flag: '🇮🇹' },
  'pt': { name: 'Português', flag: '🇵🇹' },
  'nl': { name: 'Nederlands', flag: '🇳🇱' },
  'ru': { name: 'Русский', flag: '🇷🇺' },
  'uk': { name: 'Українська', flag: '🇺🇦' },
  'hr': { name: 'Hrvatski', flag: '🇭🇷' },
  'cs': { name: 'Čeština', flag: '🇨🇿' },
  'sk': { name: 'Slovenčina', flag: '🇸🇰' },
  'hu': { name: 'Magyar', flag: '🇭🇺' },
  'ro': { name: 'Română', flag: '🇷🇴' },
  'bg': { name: 'Български', flag: '🇧🇬' },
  'el': { name: 'Ελληνικά', flag: '🇬🇷' },
  'tr': { name: 'Türkçe', flag: '🇹🇷' },
  'fi': { name: 'Suomi', flag: '🇫🇮' },
  'da': { name: 'Dansk', flag: '🇩🇰' },
  'no': { name: 'Norsk', flag: '🇳🇴' },
  'zh': { name: '中文', flag: '🇨🇳' },
  'ja': { name: '日本語', flag: '🇯🇵' },
  'ko': { name: '한국어', flag: '🇰🇷' },
  'vi': { name: 'Tiếng Việt', flag: '🇻🇳' },
  'th': { name: 'ไทย', flag: '🇹🇭' },
  'hi': { name: 'हिन्दी', flag: '🇮🇳' },
  'he': { name: 'עברית', flag: '🇮🇱' },
  'tl': { name: 'Filipino', flag: '🇵🇭' },
  'id': { name: 'Bahasa Indonesia', flag: '🇮🇩' },
  'ms': { name: 'Bahasa Melayu', flag: '🇲🇾' },
  'sw': { name: 'Kiswahili', flag: '🇰🇪' },
  'la': { name: 'Latina', flag: '🇻🇦' },
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, changeLanguage, t, settings, getAvailableLanguages, isRTL, getLocalizedPath, getCleanPath } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { eventsEnabled } = useEventsModule();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isOpen]);
  
  // Helper to create localized links
  const getLink = (path) => getLocalizedPath(path);
  
  // Handle language change with URL update
  const handleLanguageChange = (newLang) => {
    const cleanPath = getCleanPath(location.pathname);
    changeLanguage(newLang);
    
    // Build new path with language prefix
    if (newLang === settings.default_language) {
      navigate(cleanPath || '/', { replace: true });
    } else {
      navigate(`/${newLang}${cleanPath}`, { replace: true });
    }
  };

  // Get enabled languages from settings and map to full language info
  const availableLangs = getAvailableLanguages();
  const languages = availableLangs.map(code => ({
    code,
    name: ALL_SUPPORTED_LANGUAGES[code]?.name || code.toUpperCase(),
    flag: ALL_SUPPORTED_LANGUAGES[code]?.flag || '🌐',
  }));

  // Hide language selector if translation is disabled or only one language
  const showLanguageSelector = settings.translation_enabled && languages.length > 1;

  // Navigation order: Hem - Aktuellt - Församling - Fördjupning - Övrigt - Kontakt
  const navLinks = [
    { path: '/', label: t('home') },
    // Aktuellt dropdown is inserted here in the JSX
    // Församling dropdown is inserted here in the JSX
    // Fördjupning dropdown is inserted here in the JSX
    // Övrigt dropdown is inserted here in the JSX
    { path: '/contact', label: t('contact') },
  ];

  // Församling submenu items (includes Kyrkorna, Caritas, Trosundervisning, Ungdomar, Medlemskap, Album)
  const forsamlingSubmenu = [
    { path: '/churches', label: t('churches') || 'Kyrkorna' },
    { path: '/forsamling/caritas', label: t('caritas') || 'Caritas' },
    { path: '/forsamling/trosundervisning', label: t('trosundervisning') || 'Trosundervisning' },
    { path: '/forsamling/ungdomar', label: t('ungdomar') || 'Ungdomar' },
    { path: '/forsamling/album', label: t('albums') || 'Fotoalbum' },
    { path: '/ovrigt/medlemskap', label: t('membership') || 'Medlemskap' },
  ];

  // Aktuellt submenu items (Events, Bible Readings, News, Priest Corner)
  // When the Events module is disabled, label drops "/ kalendern".
  const aktuelltSubmenu = [
    {
      path: '/events',
      label: eventsEnabled
        ? (t('eventsMenuLabel') || 'Meddelanden / kalendern')
        : (t('announcementsMenuLabelShort') || 'Meddelanden'),
    },
    { path: '/liturgi', label: t('liturgiMenuLabel') || 'Liturgi' },
    { path: '/bible', label: t('bible') || 'Bibelläsningar' },
    { path: '/news', label: t('newsMenuLabel') || 'Nyheter utifrån' },
    { path: '/prastens-horna', label: t('priestCorner') || "Prästens lilla hörna" },
  ];

  // Fördjupning submenu items (Olika teman - Sakramenten - Kristna fundament - Mässordning & Böner - Att bli katolik)
  const fordjupningSubmenu = [
    { path: '/fordjupning', label: t('allArticles') || 'Olika teman' },
    { path: '/fordjupning/stiftelser', label: t('sakramenten') || 'Sakramenten' },
    { path: '/fordjupning/kristna-laran', label: t('kristnaLaran') || 'Kristna fundament' },
    { path: '/fordjupning/masses', label: t('massesAndPrayers') || 'Mässordning & Böner' },
    { path: '/fordjupning/convert', label: t('convert') || 'Att bli katolik' },
  ];

  // Övrigt submenu items
  const ovrigtSubmenu = [
    { path: '/forsamling/katalog', label: t('catalogue') || 'Katalog' },
    { path: '/ovrigt/begravning', label: t('funeral') || 'Begravning' },
    { path: '/ovrigt/kallelse', label: t('vocation') || 'Kallelse' },
    { path: '/contact#donate', label: t('donate') || 'Donera' },
  ];

  // Get current language info
  const currentLang = ALL_SUPPORTED_LANGUAGES[language] || { name: language.toUpperCase(), flag: '🌐' };

  return (
    <nav className="sticky top-0 z-50 glass-effect border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to={getLink('/')} className="flex items-center space-x-3 group">
            <img 
              src="/favicon-large.png" 
              alt="Vår Fru av Fatima" 
              className="w-12 h-12 rounded-full shadow-lg group-hover:shadow-xl transition-all duration-300"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Vår Fru av Fatima</h1>
              <p className="text-xs text-gray-600">Katolska Församlingen i Blekinge</p>
            </div>
          </Link>

          {/* Desktop Navigation - Order: Hem, Aktuellt, Församling, Fördjupning, Kontakt */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Hem */}
            <Link
              to={getLink('/')}
              className="nav-link text-gray-700 hover:text-yellow-600 font-medium text-sm"
            >
              {t('home')}
            </Link>

            {/* Aktuellt Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-link text-gray-700 hover:text-yellow-600 font-medium text-sm flex items-center gap-1">
                  {t('aktuellt') || 'Aktuellt'}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {aktuelltSubmenu.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={getLink(item.path)} className="w-full cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Församling Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-link text-gray-700 hover:text-yellow-600 font-medium text-sm flex items-center gap-1">
                  {t('forsamlingen') || 'Församlingen'}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {forsamlingSubmenu.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={getLink(item.path)} className="w-full cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Fördjupning Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-link text-gray-700 hover:text-yellow-600 font-medium text-sm flex items-center gap-1">
                  {t('fordjupning')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {fordjupningSubmenu.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={getLink(item.path)} className="w-full cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Övrigt Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="nav-link text-gray-700 hover:text-yellow-600 font-medium text-sm flex items-center gap-1">
                  {t('ovrigt') || 'Övrigt'}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {ovrigtSubmenu.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={getLink(item.path)} className="w-full cursor-pointer">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Kontakt */}
            <Link
              to={getLink('/contact')}
              className="nav-link text-gray-700 hover:text-yellow-600 font-medium text-sm"
            >
              {t('contact')}
            </Link>

            {/* Language selector moved to FloatingLanguageSelector component */}

            {/* Admin Link */}
            {isAuthenticated ? (
              <Link to="/admin">
                <Button variant="default" size="sm" className="bg-yellow-500 hover:bg-yellow-600">
                  {t('admin')}
                </Button>
              </Link>
            ) : (
              <Link to="/admin/login">
                <Button variant="outline" size="sm">
                  {t('login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation - Order: Hem, Aktuellt, Församling, Fördjupning, Kontakt */}
        {isOpen && (
          <div 
            className="md:hidden pb-4 space-y-2 fade-in overflow-y-auto mobile-menu-scroll" 
            style={{ 
              maxHeight: 'calc(100vh - 5rem - env(safe-area-inset-bottom, 0px))',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))'
            }}
            data-testid="mobile-menu"
          >
            {/* Hem */}
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('home')}
            </Link>
            
            {/* Aktuellt Section for Mobile */}
            <div className="px-4 py-2">
              <p className="text-sm font-medium text-gray-900 mb-2">{t('aktuellt') || 'Aktuellt'}</p>
              <div className="pl-4 space-y-1 border-l-2 border-blue-400">
                {aktuelltSubmenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Församling Section for Mobile */}
            <div className="px-4 py-2">
              <p className="text-sm font-medium text-gray-900 mb-2">{t('forsamlingen') || 'Församlingen'}</p>
              <div className="pl-4 space-y-1 border-l-2 border-green-400">
                {forsamlingSubmenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-1.5 text-sm text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Fördjupning Section for Mobile */}
            <div className="px-4 py-2">
              <p className="text-sm font-medium text-gray-900 mb-2">{t('fordjupning')}</p>
              <div className="pl-4 space-y-1 border-l-2 border-yellow-400">
                {fordjupningSubmenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-1.5 text-sm text-gray-600 hover:text-yellow-600 hover:bg-gray-50 rounded transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Övrigt Section for Mobile */}
            <div className="px-4 py-2">
              <p className="text-sm font-medium text-gray-900 mb-2">{t('ovrigt') || 'Övrigt'}</p>
              <div className="pl-4 space-y-1 border-l-2 border-purple-400">
                {ovrigtSubmenu.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-1.5 text-sm text-gray-600 hover:text-purple-600 hover:bg-gray-50 rounded transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Kontakt */}
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('contact')}
            </Link>
            {showLanguageSelector && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg mx-2">
                <span className="text-sm text-gray-600">{currentLang.flag} Language:</span>
                <select
                  value={language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="px-3 py-1 border rounded-lg text-sm bg-white"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="px-2 pt-2">
              {isAuthenticated ? (
                <Link to="/admin" onClick={() => setIsOpen(false)} className="block">
                  <Button className="w-full bg-yellow-500 hover:bg-yellow-600">{t('admin')}</Button>
                </Link>
              ) : (
                <Link to="/admin/login" onClick={() => setIsOpen(false)} className="block">
                  <Button variant="outline" className="w-full">{t('login')}</Button>
                </Link>
              )}
            </div>
            {/* Safe area padding for iOS browsers - increased for Safari/Chrome bottom nav */}
            <div 
              className="flex-shrink-0" 
              style={{ 
                minHeight: 'calc(80px + env(safe-area-inset-bottom, 0px))',
                paddingBottom: 'env(safe-area-inset-bottom, 20px)'
              }} 
            />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;